import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import Plot from "react-plotly.js";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

type Granularity_T = {
  label: string;
  value: string;
};

type BrandCountOverTime_T = {
  created_at_period: string;
  granularity_count?: number;
  cumulative_count: number;
};

// Granularity options
const granularityOptions: Granularity_T[] = [
  { label: "Weekly", value: "Week" },
  { label: "Monthly", value: "Month" },
  { label: "Quarterly", value: "Quarter" },
  { label: "Yearly", value: "Year" },
];

const BrandCountOverTime: React.FC = () => {
  const [granularity, setGranularity] = useState<Granularity_T>(
    granularityOptions[2]
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [chartData, setChartData] = useState<BrandCountOverTime_T[]>([]);
  const { showError } = useError();
  const { apiRequest } = useApi();

  const fetchBrandCountData = async () => {
    try {
      setLoading(true);
      const response = await apiRequest<any[]>(
        `/brand_reports/insights/brands_count_over_time/${granularity.label}`,
        { method: "GET" }
      );

      if (response.data) {
        const formattedData = response.data.map((item) => ({
          ...item,
          cumulative_count: Number(item.cumulative_count),
        }));
        setChartData(formattedData);
      }
    } catch (error) {
      console.error(error);
      showError(`Error fetching brand count data.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrandCountData();
  }, [granularity]);


  const barData = chartData.map((item) => ({
    x: item.created_at_period,
    y: item.granularity_count !== undefined ? item.granularity_count : 0,
  }));

  const lineData = chartData.map((item) => ({
    x: item.created_at_period,
    y: item.cumulative_count,
  }));

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ flexGrow: 1 }}></Box>
          <FormControl>
            <InputLabel id="select-granularity-label">Granularity</InputLabel>
            <Select
              labelId="select-granularity-label"
              label="Granularity"
              value={granularity.label}
              onChange={(e) => {
                const selectedOption = granularityOptions.find(
                  (option) => option.label === e.target.value
                );
                if (selectedOption) {
                  setGranularity(selectedOption);
                }
              }}
              style={{ width: "200px" }}
            >
              {granularityOptions.map((option) => (
                <MenuItem key={option.value} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Card className="p-0" style={{ padding: "20px" }}>
          {loading ? (
            <CircularProgress />
          ) : chartData.length === 0 ? (
            <Typography>No data available for this selection</Typography>
          ) : (
            <Plot
              config={{
                displaylogo: false,
                modeBarButtonsToRemove: ["select2d", "lasso2d"],
                responsive: true,
              }}
              data={[
                {
                  x: barData.map((item) => item.x),
                  y: barData.map((item) => item.y),
                  type: "bar",
                  name: "New Brands Created",
                  marker: { color: "#2A9D8F" },
                  hovertemplate: `<b>New Brands Created:</b> %{y}<extra></extra>`,
                },
                {
                  x: lineData.map((item) => item.x),
                  y: lineData.map((item) => item.y),
                  type: "scatter",
                  mode: "lines+markers",
                  name: "Cumulative Brands",
                  marker: { color: "#E76F51", size: 4 },
                  line: { color: "#E76F51", width: 2 },
                  yaxis: "y2",
                  hovertemplate: `<b>Cumulative Brands:</b> %{y}<extra></extra>`,
                },
              ]}
              layout={{
                legend: {
                  orientation: "h",
                  x: 0.5,
                  y: 1.1,
                  xanchor: "center",
                  yanchor: "top",
                },
                xaxis: {
                    title: {
                      text: "Time Period",
                      standoff: 15,
                    },
                    showgrid: false,
                    tickangle: granularity.value === "Week" ? -30 : -45,
                    tickfont: { size: 12 },
                    tickmode: granularity.value === "Year" ? "array" : "auto",
                    nticks: granularity.value === "Week" ? 10 : granularity.value === "Month" ? 15 : undefined,
                    tickvals:
                      granularity.value === "Year"
                        ? barData.map((item) => Math.round(Number(item.x)))
                        : undefined,
                    tickformat:
                      granularity.value === "Week" ? "%Y-W%V" :
                      granularity.value === "Month" ? "%b %Y" :
                      granularity.value === "Quarter" ? "Q%q %Y" :
                      "%Y",
                  },
                yaxis: {
                  title: "New Brands Created",
                  showgrid: true,
                  gridcolor: "#D3D3D3",
                },
                yaxis2: {
                  title: "Cumulative Brands",
                  overlaying: "y",
                  side: "right",
                  showgrid: false,
                },
                hovermode: "x unified",
                barmode: "group",
                margin: { t: 10, b: 100, l: 60, r: 60 },
                hoverlabel: {
                  bgcolor: "#FFF",
                  font: { color: "#264653" },
                  bordercolor: "#E76F51",
                  align: "left",
                },
                font: {
                  family: '"Noto Sans", serif',
                  size: 14,
                },
              }}
              style={{ width: "100%", height: "100%" }}
            />
          )}
        </Card>
      </Grid>
    </Grid>
  );
};

export default BrandCountOverTime;